import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the toggle event on the details element.
   * Closes sibling accordion-custom elements so only one is open at a time.
   * Updates the bottom featured custom cards to match the open menu item.
   */
  #handleToggle = () => {
    if (this.#suppressToggle) return;

    const parent = this.parentElement?.closest('ul, nav, .menu-drawer__inner-container') ?? this.parentElement;
    if (!parent) return;

    if (this.details.open) {
      // Close sibling accordions (suppress their toggle handlers)
      const siblings = parent.querySelectorAll('accordion-custom');
      siblings.forEach((sibling) => {
        if (sibling !== this && sibling instanceof AccordionCustom) {
          sibling.#suppressToggle = true;
          sibling.details.open = false;
          // Reset after the async toggle event has fired
          setTimeout(() => { sibling.#suppressToggle = false; }, 0);
        }
      });
    }

    // Update bottom featured custom cards
    const menuHandle = this.details.dataset.drawerMenuHandle;
    if (!menuHandle) return;

    const drawer = this.closest('.menu-drawer') || this.closest('header-drawer');
    if (!drawer) return;

    const allCardSets = drawer.querySelectorAll('[data-drawer-menu-handle].c-header-drawer-featured-custom');
    allCardSets.forEach((cardSet) => {
      if (cardSet instanceof HTMLElement) {
        cardSet.style.display = 'none';
      }
    });

    if (this.details.open) {
      const activeCards = drawer.querySelector(
        `.c-header-drawer-featured-custom[data-drawer-menu-handle="${menuHandle}"]`
      );
      if (activeCards instanceof HTMLElement) {
        activeCards.style.display = '';
      }
    }
  };

  /** @type {boolean} */
  #suppressToggle = false;

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
